import {
  Component,
  AfterViewInit,
  signal,
  effect,
  contentChildren,
  viewChild,
  ElementRef,
  output,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { trigger, state, transition, style, animate } from '@angular/animations';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { isPresent } from '@fcom/core/utils/utils';

import { CarouselTheme } from '../../carousel/enums';
import { TabComponent } from '../tab/tab.component';
import { TabTheme, TabIconPosition, TabLayoutType } from './enums';
import { CarouselComponent } from '../../carousel';
import { IconButtonSize, IconButtonTheme } from '../../buttons';

/**
 * This component will display a list of tabs. You should pass through a number of `fcom-tab`
 * components in the `ng-content`.
 *
 * @example
 * <fcom-tabs [theme]="theme" [align]="'center'">
 *   <fcom-tab [title]="tabTitle + ' ' + (i + 1)" *ngFor="let item of Arr(numberOfTabs).fill(1); let i = index">
 *     {{ tabContent }} {{ i + 1 }}
 *   </fcom-tab>
 * </fcom-tabs>
 */
@Component({
  selector: 'fcom-tabs',
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.scss'],
  animations: [
    trigger('openClose', [
      state('true', style({ height: '*' })),
      state('false', style({ height: '0px' })),
      transition('false <=> true', animate(200)),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TabsComponent implements AfterViewInit {
  readonly CarouselTheme = CarouselTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TabIconPosition = TabIconPosition;
  readonly TabLayoutType = TabLayoutType;
  readonly TabTheme = TabTheme;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;

  readonly carousel = viewChild.required<CarouselComponent>('carousel');
  readonly tabsContainer = viewChild.required<ElementRef>('tabsContainer');
  readonly tabsNavi = viewChild.required<ElementRef>('tabsNavi');

  readonly tabs = contentChildren(TabComponent);
  readonly activeTabId = signal<string | undefined>(undefined);

  /**
   * Various options for displaying compacted or full size.
   */
  theme = input<TabTheme>(TabTheme.DEFAULT);

  /**
   * Various options of layout of tabs.
   */
  layoutType = input<TabLayoutType>(TabLayoutType.DEFAULT);

  /**
   * How the tabs should be aligned in the container
   */
  align = input<'left' | 'center' | 'right'>('left');

  /**
   * Aria Label for the previous button in the carousel
   */
  prevButtonAriaLabel = input<string>();

  /**
   * Aria Label for the next button in the carousel
   */
  nextButtonAriaLabel = input<string>();

  /**
   * Size of the carousel navigation buttons.
   */
  carouselButtonSize = input<IconButtonSize>(IconButtonSize.MEDIUM);

  /**
   * Carousel has container shadow.
   */
  carouselHasContainerShadow = input<boolean>(true);

  hasGreyBackground = input<boolean>(false);
  tabStyleOverrideClasses = input<string>('');
  bottomBorder = input<boolean>(true);
  whiteSpaceNowrap = input<boolean>(false);
  showSubtitle = input<boolean>(true);

  clickedTab = output<{ index: number; data: unknown }>();

  constructor() {
    effect(() => {
      const currentTabs = this.tabs();
      if (currentTabs.length === 0) {
        return;
      }

      const explicitlyActive = currentTabs.find((t) => t.active() && !t.disabled());
      if (explicitlyActive) {
        this.activeTabId.set(explicitlyActive.tabId);
      } else if (!currentTabs.some((t) => t.tabId === this.activeTabId())) {
        const firstEnabled = currentTabs.find((t) => !t.disabled());
        this.activeTabId.set(firstEnabled?.tabId);
      }
    });
  }

  ngAfterViewInit(): void {
    this.scrollToSelectedTab();
  }

  selectTab(tab: TabComponent): void {
    if (isPresent(tab)) {
      this.activeTabId.set(tab.tabId);
      const currentTabs = this.tabs();
      this.clickedTab.emit({
        index: currentTabs.findIndex((t) => t.tabId === tab.tabId),
        data: tab.data(),
      });
    }
  }

  scrollToSelectedTab(): void {
    const activeTab = this.tabsNavi().nativeElement.querySelector('.tab--active');
    if (
      activeTab &&
      (activeTab.offsetLeft as number) + (activeTab.offsetWidth as number) >
        this.tabsContainer().nativeElement.offsetWidth
    ) {
      this.carousel().scrollTo(activeTab.offsetLeft);
    }
  }
}
